import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  NumberInput,
  Pagination,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import React, { useEffect, useState } from "react";
import {
  repositoryDelivery,
  repositoryPos,
  repositoryWebClient,
} from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../../../model/MessageResponse";
import CPUPromotionWeb from "./CPUPromotionWeb";
import UIGiftPromotion from "./UIGiftPromotion";

interface CTKMModalContentProps {
  row: any;
  customDiscount: number | null;
  setCustomDiscount: (value: number | null) => void;
  setListAdd: any;
  location: any;
}

const CTKMModalContent: React.FC<CTKMModalContentProps> = ({
  row,
  customDiscount,
  setCustomDiscount,
  setListAdd,
  location,
}) => {
  const [warning, setWarning] = React.useState<string | null>(null);
  const [localCTKMCode, setLocalCTKMCode] = React.useState(
    row.original.attribute33 || "",
  );
  const [localCTKMDescription, setLocalCTKMDescription] = React.useState(
    row.original.attribute34 || "",
  );
  const [localCustomDiscount, setLocalCustomDiscount] =
    React.useState(customDiscount);
  const [discountPercent, setDiscountPercent] = React.useState<number | null>(
    null,
  );
  const [promotionWeb, setPromotionWeb] = useState<string>("");
  const [dataProductWeb, setDataProductWeb] = useState<any>({});

  // State cho popup CTKM đang áp dụng
  const [opened, { open, close }] = useDisclosure(false);
  const [ctkemData, setCTKEMData] = React.useState<any[]>([]);
  const [loadingCTKEM, setLoadingCTKEM] = React.useState(false);
  const [selectedCTKMs, setSelectedCTKMs] = React.useState<any[]>([]);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(false);
  // State cho tìm kiếm CTKM
  const [searchOpened, { open: openSearch, close: closeSearch }] =
    useDisclosure(false);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalRecords, setTotalRecords] = React.useState(0);
  const pageSize = 20;

  //State cho chọn CTKM
  const [isSelectCTKM, setSelectCTKM] = React.useState<boolean>(false);
  const [isSelectCTKMTN, setSelectCTKMTN] = React.useState<boolean>(
    row.original.isPromotionType && row.original.isPromotionType === "Y"
      ? true
      : false,
  );

  const isViewMode =
    location?.state?.actionType === "view" ||
    location?.state?.actionType === "payment";

  // Debug log để kiểm tra view mode
  console.log(
    "CTKMModalContent - isViewMode:",
    isViewMode,
    "actionType:",
    location?.state?.actionType,
  );

  // Function để chọn/bỏ chọn CTKM
  const handleSelectCTKM = (item: any) => {
    const isAlreadySelected = selectedCTKMs.some(
      (selected) => selected.maCtkm === item.maCtkm,
    );

    if (isAlreadySelected) {
      // Bỏ chọn nếu đã được chọn
      setSelectedCTKMs((prev) =>
        prev.filter((selected) => selected.maCtkm !== item.maCtkm),
      );
    } else {
      // Thêm vào danh sách đã chọn
      setSelectedCTKMs((prev) => [...prev, item]);
    }
  };

  // Function để xác nhận chọn các CTKM
  const handleConfirmSelection = () => {
    if (selectedCTKMs.length === 0) {
      NotificationExtension.Fails("Vui lòng chọn ít nhất một CTKM");
      return;
    }

    // Ghép các mã CTKM bằng dấu ";" và xuống dòng
    const combinedCodes = selectedCTKMs
      .map((item) => item.maCtkm || "")
      .join(";\n");
    const combinedNames = selectedCTKMs
      .map((item) => item.tenCtkm || "")
      .join(";\n");

    setLocalCTKMCode(combinedCodes);
    setLocalCTKMDescription(combinedNames);
    setSelectedCTKMs([]); // Reset selection
    close(); // Đóng modal sau khi chọn
    NotificationExtension.Success(`Đã chọn ${selectedCTKMs.length} CTKM`);
  };

  // Function để call API lấy thông tin CTKM đang áp dụng
  const handleCheckCTKM = async () => {
    setLoadingCTKEM(true);
    try {
      const response = await repositoryDelivery.get<MessageResponse<any>>(
        `/api/v1/TblPromotion/get-promotions-by-item-code?inUse=true&itemCode=${row.original.itemCode}`,
      );

      if (response && response.success) {
        setCTKEMData(response.data || []);
        open();
      } else {
        NotificationExtension.Fails("Không thể lấy thông tin CTKM");
      }
    } catch (error) {
      console.error("Error fetching CTKM data:", error);
      NotificationExtension.Fails("Lỗi khi lấy thông tin CTKM");
    } finally {
      setLoadingCTKEM(false);
    }
  };
  // Gen Code CTKM tự nhập
  const handleGenCodePromotion = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/GenerateCtkmCode/generate-ctkm?length=11",
    );

    if (res && res.success) {
      setLocalCTKMCode(res.code);
      setLocalCTKMDescription("");
      setLocalCustomDiscount(null);
      setDiscountPercent(null);
    }
  };

  // Function để tìm kiếm CTKM
  const handleSearchCTKM = async (page: number = 1, keyword: string = "") => {
    setLoadingSearch(true);
    try {
      const skip = (page - 1) * pageSize;
      const searchParam = keyword.trim()
        ? `&KeySearch=${encodeURIComponent(keyword.trim())}`
        : "";

      const response = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/tblPromotion/get-list?Skip=${skip}&Take=${pageSize}&ApproveStatus=2&IsRunning=true${searchParam}`,
      );

      if (response && response.success) {
        setSearchResults(response.data || []);
        setCurrentPage(page);

        // Tính toán phân trang (giả sử API trả về total count)
        const total = response.totalCount || response.data?.length || 0;
        setTotalRecords(total);
        setTotalPages(Math.ceil(total / pageSize));
      } else {
        setSearchResults([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error searching CTKM:", error);
      NotificationExtension.Fails("Lỗi khi tải danh sách CTKM");
      setSearchResults([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoadingSearch(false);
    }
  };

  // Function để load CTKM khi mở modal
  const handleOpenSearchModal = () => {
    setSearchKeyword("");
    setCurrentPage(1);
    openSearch();
    // Load tất cả CTKM mặc định khi mở modal
    handleSearchCTKM(1, "");
  };

  // Function để xử lý search theo keyword
  const handleKeywordSearch = () => {
    setCurrentPage(1);
    handleSearchCTKM(1, searchKeyword);
  };

  // Function để thay đổi trang
  const handlePageChange = (page: number) => {
    handleSearchCTKM(page, searchKeyword);
  };

  // Function để chọn CTKM từ kết quả tìm kiếm
  const handleSelectSearchResult = (item: any) => {
    setSelectCTKM(true);
    setSelectCTKMTN(false);
    setLocalCTKMCode(item.code || item.maCtkm || "");
    setLocalCTKMDescription(
      item.note || item.tenCtkm || item.name || item.description || "",
    );
    setLocalCustomDiscount(null);
    setDiscountPercent(null);
    closeSearch();
    setSearchKeyword("");
    NotificationExtension.Success("Đã chọn CTKM từ kết quả tìm kiếm");
  };

  // Hàm cập nhật giá trị giảm giá theo số tiền
  const handleDiscountChange = (value: number | null) => {
    if (isViewMode) return; // Chặn thay đổi trong view mode

    if (value !== null && value > row.original.priceOnPriceList) {
      setWarning("KM không được lớn hơn giá sản phẩm");
      return;
    }
    setWarning(null);
    setLocalCustomDiscount(value);
    setCustomDiscount(value);

    // Reset % khi nhập số tiền trực tiếp
    if (value !== null && row.original.priceOnPriceList > 0) {
      const calculatedPercent = (value / row.original.priceOnPriceList) * 100;
      setDiscountPercent(Math.round(calculatedPercent * 100) / 100); // Làm tròn 2 chữ số
    } else {
      setDiscountPercent(null);
    }

    setListAdd((prevData: any[]) =>
      prevData.map((item, i) => {
        if (i === row.index) {
          const updatedDiscountAmount = value || 0;
          const updatedPriceFinal =
            row.original.priceOnPriceList -
            updatedDiscountAmount -
            (row.original.discountAdded || 0);

          return {
            ...item,
            discountAmount: updatedDiscountAmount,
            priceFinal: updatedPriceFinal > 0 ? updatedPriceFinal : 0,
          };
        }
        return item;
      }),
    );
  };

  // Hàm cập nhật giá trị giảm giá theo %
  const handleDiscountPercentChange = (value: number | null) => {
    if (isViewMode) return;

    if (value !== null && (value < 0 || value > 100)) {
      setWarning("% giảm giá phải từ 0 đến 100");
      return;
    }

    setWarning(null);
    setDiscountPercent(value);

    // Tính số tiền giảm giá dựa trên %
    if (value !== null && row.original.priceOnPriceList > 0) {
      const calculatedDiscount = Math.round(
        (row.original.priceOnPriceList * value) / 100,
      );
      setLocalCustomDiscount(calculatedDiscount);
      setCustomDiscount(calculatedDiscount);

      setListAdd((prevData: any[]) =>
        prevData.map((item, i) => {
          if (i === row.index) {
            const updatedDiscountAmount = calculatedDiscount || 0;
            const updatedPriceFinal =
              row.original.priceOnPriceList -
              updatedDiscountAmount -
              (row.original.discountAdded || 0);

            return {
              ...item,
              discountAmount: updatedDiscountAmount,
              priceFinal: updatedPriceFinal > 0 ? updatedPriceFinal : 0,
            };
          }
          return item;
        }),
      );
    } else {
      setLocalCustomDiscount(null);
      setCustomDiscount(null);
    }
  };

  // Hàm lưu thông tin CTKM
  const handleSaveCTKM = () => {
    setListAdd((prevData: any[]) =>
      prevData.map((item, i) => {
        if (i === row.index) {
          return {
            ...item,
            attribute33: localCTKMCode,
            attribute34: localCTKMDescription,
            isPromotionType: isSelectCTKMTN ? "Y" : "N",
          };
        }
        return item;
      }),
    );
    modals.closeAll();
    NotificationExtension.Success("Đã lưu thông tin CTKM thành công!");
  };

  // Hàm xử lý khi bấm nút Hủy
  const handleCancel = () => {
    // Reset về giá trị ban đầu
    setLocalCTKMCode(row.original.attribute33 || "");
    setLocalCTKMDescription(row.original.attribute34 || "");
    setLocalCustomDiscount(customDiscount);
    setDiscountPercent(null);
    setWarning(null);

    // Reset lại giá trị trong listAdd về giá trị ban đầu
    setListAdd((prevData: any[]) =>
      prevData.map((item, i) => {
        if (i === row.index) {
          const originalDiscountAmount = customDiscount || 0;
          const updatedPriceFinal =
            row.original.priceOnPriceList -
            originalDiscountAmount -
            (row.original.discountAdded || 0);

          return {
            ...item,
            discountAmount: originalDiscountAmount,
            priceFinal: updatedPriceFinal > 0 ? updatedPriceFinal : 0,
            attribute33: row.original.attribute33 || "",
            attribute34: row.original.attribute34 || "",
          };
        }
        return item;
      }),
    );

    modals.closeAll();
  };

  //Lấy data mô tả CTKM trên web
  const getDataPromotionWeb = async () => {
    const res = await repositoryWebClient.get<MessageResponse<any>>(
      `/api-client/api/v1/TblItemV/get-item-slug-v2?itemCode=${row.original.itemCode}`,
    );

    if (res && res.success) {
      const data = res.data ? res.data : "";
      const promotionWeb = data?.attribute10 || "";
      setDataProductWeb(res.data);
      setPromotionWeb(String(promotionWeb));
    }
  };

  //Lấy data CTKM theo mã sản phẩm POS
  // const getDataPromotionWithItemCode = async () => {
  //   const res = await repositoryPos.post<MessageResponse<any>>(
  //     `/api/v1/TblItem/item-promotion-web`,
  //     {
  //       branch: 0,
  //       itemCode: row.original.itemCode,
  //       price: 0,
  //     },
  //   );

  //   if (res && res.success) {
  //     const data = res.data;
  //     console.log(data);
  //   }
  // };

  useEffect(() => {
    if (!isFirstRender) {
      setIsFirstRender(true);
      handleDiscountChange(customDiscount);
    }

    getDataPromotionWeb();
    // getDataPromotionWithItemCode();
  }, []);

  useEffect(() => {
    if (isSelectCTKMTN && isFirstRender) {
      handleGenCodePromotion();
    }

    if (!isSelectCTKM && !isSelectCTKMTN && isFirstRender) {
      setLocalCTKMCode("");
      setLocalCTKMDescription("");
      setLocalCustomDiscount(null);
      setDiscountPercent(null);
    }
  }, [isSelectCTKMTN]);

  return (
    <Box style={{ padding: "16px" }}>
      <Stack gap="md">
        {/* Thông tin sản phẩm */}
        <Box
          style={{
            padding: "12px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          <Text fw={600} size="sm" c="black">
            Sản phẩm: {row.original.itemName}
          </Text>
          <Text size="sm" c="black">
            Mã: {row.original.itemCode} | Giá:{" "}
            {row.original.priceOnPriceList?.toLocaleString()}đ
          </Text>
        </Box>

        {/* Mã CTKM */}
        <Box>
          <Flex justify={"space-between"}>
            <Text fw={600} size="sm" mb="xs">
              Mã CTKM <span style={{ color: "red" }}>*</span>
            </Text>
            <Checkbox
              label={"CTKM tự nhập"}
              checked={isSelectCTKMTN}
              onClick={() => {
                setSelectCTKMTN(!isSelectCTKMTN);
                setSelectCTKM(false);
              }}
            />
          </Flex>
          <TextInput
            value={localCTKMCode}
            onChange={(e) => {
              if (isViewMode) return;
              setLocalCTKMCode(e.target.value);
            }}
            placeholder="Nhập mã chương trình khuyến mãi"
            size="md"
            // readOnly={isViewMode}
            readOnly
            variant="filled"
            style={
              isViewMode
                ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                : {}
            }
          />
        </Box>

        {/* Mô tả CTKM */}
        <Box>
          <Text fw={600} size="sm" mb="xs">
            Mô tả CTKM <span style={{ color: "red" }}>*</span>
          </Text>
          <Textarea
            value={localCTKMDescription}
            onChange={(e) => {
              if (isViewMode) return;
              setLocalCTKMDescription(e.target.value);
            }}
            placeholder="Nhập mô tả chương trình khuyến mãi"
            size="md"
            rows={4}
            variant={!isSelectCTKMTN ? "filled" : "default"}
            readOnly={isViewMode || !isSelectCTKMTN}
            // disabled={isViewMode}
            styles={{
              input: {
                minHeight: "100px",
                maxHeight: "200px",
                overflowY: "auto",
                resize: "none",
              },
            }}
            style={isViewMode ? { pointerEvents: "none" } : {}}
          />
        </Box>

        {/* Mô tả CTKM trên web */}
        {dataProductWeb && (
          <Box>
            <Text fw={600} size="sm" mb="xs">
              Mô tả CTKM tại hacom.vn
            </Text>
            <ScrollArea h={175}>
              {dataProductWeb?.attribute14 &&
                String(dataProductWeb?.attribute14).length > 0 && (
                  <UIGiftPromotion
                    attr14={dataProductWeb?.attribute14}
                    itemCode={row.original.itemCode}
                  />
                )}
              {String(row.original.itemCode).length <= 8 &&
                String(row.original.itemCode).includes("CPU") && (
                  <CPUPromotionWeb data={dataProductWeb} />
                )}

              <div dangerouslySetInnerHTML={{ __html: promotionWeb }} />
            </ScrollArea>
          </Box>
        )}

        {/* Giảm giá tự nhập */}
        <Box>
          <Text fw={600} size="sm" mb="xs">
            Giảm giá tự nhập
          </Text>
          <Flex gap="md" align="start">
            <Box style={{ flex: 1 }}>
              <NumberInput
                value={localCustomDiscount ?? ""}
                onChange={(value) => {
                  if (isViewMode) return;
                  handleDiscountChange(
                    typeof value === "string"
                      ? parseFloat(value) || null
                      : value,
                  );
                }}
                onKeyDown={(event) => {
                  // Chặn ký tự dấu trừ (-) và các ký tự không mong muốn
                  if (event.key === "-" || event.key === "Minus") {
                    event.preventDefault();
                  }
                }}
                onPaste={(event) => {
                  // Chặn paste nội dung có chứa dấu âm
                  const paste = (
                    event.clipboardData || (window as any).clipboardData
                  ).getData("text");
                  if (paste.includes("-")) {
                    event.preventDefault();
                  }
                }}
                placeholder="Nhập số tiền giảm giá"
                size="md"
                hideControls
                thousandSeparator="."
                decimalSeparator=","
                min={0}
                max={row.original.priceOnPriceList}
                variant={
                  isViewMode ||
                  !localCTKMCode.trim() ||
                  !localCTKMDescription.trim()
                    ? "filled"
                    : "default"
                }
                readOnly={
                  isViewMode ||
                  !localCTKMCode.trim() ||
                  !localCTKMDescription.trim()
                }
                style={
                  isViewMode
                    ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                    : {}
                }
              />
            </Box>

            <Box style={{ width: "150px" }}>
              <NumberInput
                value={discountPercent ?? ""}
                onChange={(value) => {
                  if (isViewMode) return;
                  handleDiscountPercentChange(
                    typeof value === "string"
                      ? parseFloat(value) || null
                      : value,
                  );
                }}
                onKeyDown={(event) => {
                  if (event.key === "-" || event.key === "Minus") {
                    event.preventDefault();
                  }
                }}
                onPaste={(event) => {
                  const paste = (
                    event.clipboardData || (window as any).clipboardData
                  ).getData("text");
                  if (paste.includes("-")) {
                    event.preventDefault();
                  }
                }}
                placeholder="% giảm"
                size="md"
                hideControls
                thousandSeparator="."
                decimalSeparator=","
                min={0}
                max={100}
                suffix="%"
                variant={
                  isViewMode ||
                  !localCTKMCode.trim() ||
                  !localCTKMDescription.trim()
                    ? "filled"
                    : "default"
                }
                readOnly={
                  isViewMode ||
                  !localCTKMCode.trim() ||
                  !localCTKMDescription.trim()
                }
                style={
                  isViewMode
                    ? { pointerEvents: "none", backgroundColor: "#f8f9fa" }
                    : {}
                }
              />
            </Box>
          </Flex>
          {(!localCTKMCode.trim() || !localCTKMDescription.trim()) && (
            <Text size="xs" c="orange" mt="xs">
              Vui lòng điền đầy đủ thông tin CTKM để có thể nhập giảm giá
            </Text>
          )}
        </Box>

        {/* Cảnh báo */}
        {warning && (
          <Box
            style={{
              color: "orange",
              fontSize: "14px",
              textAlign: "center",
              padding: "8px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
            }}
          >
            {warning}
          </Box>
        )}

        {/* Buttons */}
        <Flex justify="space-between" align="center" mt="md">
          {!isViewMode && (
            <Flex gap="xs">
              <Button
                size="sm"
                variant="light"
                color="blue"
                onClick={handleOpenSearchModal}
              >
                Tìm kiếm CTKM
              </Button>
              <Button
                size="sm"
                variant="outline"
                color="blue"
                onClick={handleCheckCTKM}
                loading={loadingCTKEM}
              >
                Kiểm tra CTKM theo SP
              </Button>
            </Flex>
          )}

          <Flex gap="md" ml={isViewMode ? 0 : "auto"}>
            <Button variant="outline" color="gray" onClick={handleCancel}>
              {isViewMode ? "Đóng" : "Hủy"}
            </Button>
            {!isViewMode && (
              <Button
                color="blue"
                onClick={handleSaveCTKM}
                disabled={!localCTKMCode.trim() || !localCTKMDescription.trim()}
              >
                Lưu thông tin
              </Button>
            )}
          </Flex>
        </Flex>
      </Stack>

      {/* Modal hiển thị CTKM đang áp dụng */}
      <Modal
        opened={opened}
        onClose={close}
        title={`Danh sách CTKM đang áp dụng (Đã chọn: ${selectedCTKMs.length})`}
        size="lg"
        centered
        zIndex={1050}
      >
        <ScrollArea h={400}>
          {ctkemData.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>STT</Table.Th>
                  <Table.Th>Mã CTKM</Table.Th>
                  <Table.Th>Tên CTKM</Table.Th>
                  <Table.Th>Hành động</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {ctkemData.map((item, index) => {
                  const isSelected = selectedCTKMs.some(
                    (selected) => selected.maCtkm === item.maCtkm,
                  );
                  return (
                    <Table.Tr
                      key={index}
                      style={{
                        backgroundColor: isSelected ? "#e7f5ff" : "transparent",
                      }}
                    >
                      <Table.Td>{index + 1}</Table.Td>
                      <Table.Td>{item.maCtkm || "N/A"}</Table.Td>
                      <Table.Td>{item.tenCtkm || "N/A"}</Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant={isSelected ? "filled" : "outline"}
                          color={isSelected ? "red" : "blue"}
                          onClick={() => handleSelectCTKM(item)}
                          disabled={isViewMode}
                        >
                          {isSelected ? "Bỏ chọn" : "Chọn"}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          ) : (
            <Text ta="center" c="dimmed" py="xl">
              Không có CTKM nào đang áp dụng cho sản phẩm này
            </Text>
          )}
        </ScrollArea>

        {/* Buttons cho modal */}
        {ctkemData.length > 0 && (
          <Flex justify="space-between" align="center" mt="md" gap="md">
            <Button
              variant="outline"
              color="gray"
              onClick={() => {
                setSelectedCTKMs([]);
                close();
              }}
            >
              Hủy
            </Button>
            <Button
              color="blue"
              onClick={handleConfirmSelection}
              disabled={selectedCTKMs.length === 0 || isViewMode}
            >
              Xác nhận chọn ({selectedCTKMs.length})
            </Button>
          </Flex>
        )}
      </Modal>

      {/* Modal tìm kiếm CTKM */}
      <Modal
        opened={searchOpened}
        onClose={closeSearch}
        title="Tìm kiếm CTKM"
        size="xl"
        centered
        zIndex={1060}
        padding="xl"
      >
        <Stack gap="lg">
          {/* Ô tìm kiếm */}
          <Box>
            <Text fw={500} mb="sm" size="sm" c="dimmed">
              Danh sách tất cả CTKM (có thể tìm kiếm theo từ khóa)
            </Text>
            <Flex gap="md" align="end">
              <TextInput
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Nhập mã CTKM hoặc mô tả để lọc kết quả..."
                size="md"
                style={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleKeywordSearch();
                  }
                }}
              />
              <Button
                onClick={handleKeywordSearch}
                loading={loadingSearch}
                size="md"
                style={{ minWidth: "100px" }}
              >
                Tìm kiếm
              </Button>
            </Flex>
          </Box>

          {/* Kết quả tìm kiếm */}
          <Box>
            <Flex justify="space-between" align="center" mb="md">
              <Text fw={500} size="sm" c="dimmed">
                Danh sách CTKM {totalRecords > 0 && `(${totalRecords} kết quả)`}
              </Text>
              {searchKeyword.trim() && (
                <Button
                  size="xs"
                  variant="light"
                  color="gray"
                  onClick={() => {
                    setSearchKeyword("");
                    handleSearchCTKM(1, "");
                  }}
                >
                  Xóa bộ lọc
                </Button>
              )}
            </Flex>
            <ScrollArea
              h={400}
              style={{ border: "1px solid #e9ecef", borderRadius: "8px" }}
            >
              {loadingSearch ? (
                <Box style={{ padding: "60px 20px", textAlign: "center" }}>
                  <Text ta="center" c="dimmed" size="md">
                    Đang tải danh sách CTKM...
                  </Text>
                </Box>
              ) : searchResults.length > 0 ? (
                <Table striped highlightOnHover>
                  <Table.Thead
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#f8f9fa",
                      zIndex: 1,
                    }}
                  >
                    <Table.Tr>
                      <Table.Th
                        style={{
                          width: "60px",
                          textAlign: "center",
                          padding: "12px 8px",
                        }}
                      >
                        STT
                      </Table.Th>
                      <Table.Th style={{ width: "160px", padding: "12px" }}>
                        Mã CTKM
                      </Table.Th>
                      <Table.Th style={{ minWidth: "350px", padding: "12px" }}>
                        Tên/Mô tả CTKM
                      </Table.Th>
                      <Table.Th
                        style={{
                          width: "100px",
                          textAlign: "center",
                          padding: "12px",
                        }}
                      >
                        Hành động
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {searchResults.map((item, index) => (
                      <Table.Tr key={index} style={{ cursor: "pointer" }}>
                        <Table.Td
                          style={{ textAlign: "center", padding: "12px 8px" }}
                        >
                          <Text size="sm" fw={500}>
                            {(currentPage - 1) * pageSize + index + 1}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ padding: "12px" }}>
                          <Text size="sm" fw={600} c="blue">
                            {item.code || item.maCtkm || "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ padding: "12px" }}>
                          <Text
                            size="sm"
                            lineClamp={3}
                            style={{ lineHeight: 1.4 }}
                          >
                            {item.note ||
                              item.tenCtkm ||
                              item.name ||
                              item.description ||
                              "N/A"}
                          </Text>
                        </Table.Td>
                        <Table.Td
                          style={{ textAlign: "center", padding: "12px" }}
                        >
                          <Button
                            size="xs"
                            variant="outline"
                            color="blue"
                            onClick={() => handleSelectSearchResult(item)}
                            style={{ minWidth: "60px" }}
                          >
                            Chọn
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Box style={{ padding: "60px 20px", textAlign: "center" }}>
                  <Text ta="center" c="dimmed" size="md">
                    {searchKeyword.trim()
                      ? "Không tìm thấy CTKM nào phù hợp với từ khóa tìm kiếm"
                      : "Không có CTKM nào"}
                  </Text>
                  {searchKeyword.trim() && (
                    <Text ta="center" c="dimmed" size="sm" mt="xs">
                      Thử với từ khóa khác hoặc xóa bộ lọc để xem tất cả
                    </Text>
                  )}
                </Box>
              )}
            </ScrollArea>

            {/* Phân trang */}
            {totalPages > 1 && (
              <Flex justify="center" mt="md">
                <Pagination
                  value={currentPage}
                  onChange={handlePageChange}
                  total={totalPages}
                  disabled={loadingSearch}
                  size="sm"
                />
              </Flex>
            )}
          </Box>

          {/* Buttons cho modal tìm kiếm */}
          <Flex
            justify="end"
            gap="md"
            pt="md"
            style={{ borderTop: "1px solid #e9ecef" }}
          >
            <Button
              variant="outline"
              color="gray"
              onClick={() => {
                setSearchKeyword("");
                setSearchResults([]);
                closeSearch();
              }}
              size="md"
              style={{ minWidth: "80px" }}
            >
              Đóng
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </Box>
  );
};

export default CTKMModalContent;
